import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import './Orders.css';

const apiUrl = process.env.REACT_APP_API_URL || 'https://storefront-tsq2.onrender.com';
const socket = io(apiUrl, {
  autoConnect: true,
  transports: ['polling', 'websocket'], // Allow both polling and websocket
  upgrade: true,
  rememberUpgrade: true,
  timeout: 20000
});

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('new');
  // Track checked items for each order by orderId
  const [checkedItems, setCheckedItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    // Fetch initial orders
    fetchOrders();

    // Socket connection event handlers
    socket.on('connect', () => {
      console.log('Connected to server');
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    // Listen for new orders
    socket.on('newOrder', (order) => {
      setOrders(prevOrders => [order, ...prevOrders]);
      setNotification('A new order has been placed!');
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      }
    });

    // Listen for order updates
    socket.on('orderUpdated', (updatedOrder) => {
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order._id === updatedOrder._id ? updatedOrder : order
        )
      );
    });

    return () => {
      socket.off('connect');
      socket.off('connect_error');
      socket.off('newOrder');
      socket.off('orderUpdated');
    };
  }, []);

  useEffect(() => {
    // Initialize checkedItems state for each order
    const initialChecked = {};
    orders.forEach(order => {
      if (!checkedItems[order._id]) {
        initialChecked[order._id] = Array(order.items.length).fill(false);
      } else {
        initialChecked[order._id] = checkedItems[order._id];
      }
    });
    setCheckedItems(initialChecked);
    // eslint-disable-next-line
  }, [orders]);

  useEffect(() => {
    if (notification) {
      if (audioRef.current) {
        audioRef.current.loop = true;
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      }
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  }, [notification]);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/orders`);
      if (!response.ok) throw new Error('Failed to fetch orders');
      const data = await response.json();
      setOrders(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${apiUrl}/api/orders/${orderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!response.ok) throw new Error('Failed to update order');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCheckboxChange = (orderId, idx) => {
    setCheckedItems(prev => {
      const updated = { ...prev };
      updated[orderId] = [...(updated[orderId] || [])];
      updated[orderId][idx] = !updated[orderId][idx];
      return updated;
    });
  };

  const allChecked = (orderId, items) => {
    return checkedItems[orderId] && checkedItems[orderId].every(Boolean) && checkedItems[orderId].length === items.length;
  };

  // Filter orders by tab
  let filteredOrders = [];
  if (activeTab === 'new') {
    filteredOrders = orders.filter(order => order.status === 'pending');
  } else if (activeTab === 'preparing') {
    filteredOrders = orders.filter(order => order.status === 'preparing');
  } else if (activeTab === 'finished') {
    filteredOrders = orders.filter(order => order.status === 'ready');
  }

  // Further filter by search query
  const searchedOrders = filteredOrders.filter(order => {
    const query = searchQuery.toLowerCase();
    return (
      order.name?.toLowerCase().includes(query) ||
      order.phone?.toLowerCase().includes(query) ||
      order.items.some(item => item.name.toLowerCase().includes(query))
    );
  });

  const handleNotificationClick = () => {
    setNotification(null);
    setActiveTab('new');
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  if (loading) return <div>Loading orders...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="orders-container">
      <audio ref={audioRef} src="/mixkit-bell-notification-933.mp3" preload="auto" />
      {notification && (
        <div style={{
          background: '#8B0000',
          color: '#fff',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '18px',
          textAlign: 'center',
          fontWeight: 600,
          fontSize: '1.1rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}>
          {notification}
          <button
            onClick={handleNotificationClick}
            style={{
              marginLeft: 16,
              padding: '6px 18px',
              background: '#fff',
              color: '#8B0000',
              border: 'none',
              borderRadius: 6,
              fontWeight: 700,
              fontSize: '1rem',
              cursor: 'pointer',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
            }}
          >
            View New Orders
          </button>
        </div>
      )}
      <h1>Orders</h1>
      <input
        type="text"
        placeholder="Search by name, phone, or item..."
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        style={{ width: '100%', maxWidth: 400, margin: '0 auto 20px auto', display: 'block', padding: 10, borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
      />
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'new' ? 'active' : ''}`}
          onClick={() => setActiveTab('new')}
        >
          New Orders
        </button>
        <button 
          className={`tab ${activeTab === 'preparing' ? 'active' : ''}`}
          onClick={() => setActiveTab('preparing')}
        >
          Preparing Orders
        </button>
        <button 
          className={`tab ${activeTab === 'finished' ? 'active' : ''}`}
          onClick={() => setActiveTab('finished')}
        >
          Finished Orders
        </button>
      </div>

      <div className="orders-list">
        {searchedOrders.map(order => (
          <div key={order._id} className="order-card">
            <div className="order-header">
              <h3>Order #{order._id.slice(-6)}</h3>
              <span className={`status ${order.status}`}>{order.status}</span>
            </div>
            <div className="order-details">
              <p><strong>Customer:</strong> {order.name}</p>
              {order.phone && <p><strong>Phone:</strong> {order.phone}</p>}
              <p><strong>Total:</strong> ${((order.items.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0) * 1.08375).toFixed(2))}</p>
              <p><strong>Time:</strong> {new Date(order.createdAt).toLocaleString()}</p>
            </div>
            <div className="order-items">
              <h4>Items:</h4>
              <ul>
                {order.items.map((item, index) => (
                  <li key={index} style={{display: 'flex', alignItems: 'flex-start', flexDirection: 'column', paddingBottom: 8}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                      {activeTab === 'preparing' && (
                        <input
                          type="checkbox"
                          checked={checkedItems[order._id] ? checkedItems[order._id][index] : false}
                          onChange={() => handleCheckboxChange(order._id, index)}
                          disabled={order.status === 'ready'}
                        />
                      )}
                      <span>{(item.quantity || 1)} × {item.name}</span>
                    </div>
                    {item.options && (
                      <div style={{ fontSize: '0.98em', color: '#555', marginLeft: activeTab === 'preparing' ? 36 : 18, marginTop: 2 }}>
                        {item.options.bread && <div><strong>Bread:</strong> {item.options.bread === 'hero' ? 'Hero (+$2.00)' : item.options.bread.charAt(0).toUpperCase() + item.options.bread.slice(1)}</div>}
                        {item.options.wrapType && <div><strong>Wrap Type:</strong> {item.options.wrapType === 'tomato' ? 'Tomato Basil' : item.options.wrapType.charAt(0).toUpperCase() + item.options.wrapType.slice(1)}</div>}
                        {item.options.toastType && <div><strong>Toast:</strong> {item.options.toastType === 'none' ? 'No Toast' : item.options.toastType.charAt(0).toUpperCase() + item.options.toastType.slice(1)}</div>}
                        {item.options.platterOrWrap && <div><strong>Type:</strong> {item.options.platterOrWrap}</div>}
                        {(item.options.salt || item.options.pepper || item.options.ketchup) && (
                          <div><strong>Condiments:</strong> {['salt', 'pepper', 'ketchup'].filter(opt => item.options[opt]).map(opt => opt.charAt(0).toUpperCase() + opt.slice(1)).join(', ')}</div>
                        )}
                        {item.options.combo && <div><strong>Combo:</strong> Yes (includes fries)</div>}
                        {item.options.specialInstructions && <div><strong>Special Instructions:</strong> {item.options.specialInstructions}</div>}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            {['new', 'preparing'].includes(activeTab) && (
              <div className="order-actions">
                <select
                  value={order.status}
                  onChange={(e) => updateOrderStatus(order._id, e.target.value)}
                  disabled={order.status === 'ready'}
                >
                  {activeTab === 'new' && <option value="pending">Pending</option>}
                  <option value="preparing">Preparing</option>
                  <option value="ready" disabled={!allChecked(order._id, order.items)}>Ready</option>
                </select>
              </div>
            )}
          </div>
        ))}
        {searchedOrders.length === 0 && (
          <div className="no-orders">
            No {activeTab} orders found
          </div>
        )}
      </div>
    </div>
  );
}

export default Orders; 